import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Row, Col, Form, Button, Card, Tab, Tabs, Spinner, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';
//import spinnerIcon from './/./../assets/img/loadingSpinner.gif'
import SpinnerComponent from '../components/progressbar/SpinnerComponent.js'
import PaginationComponent from '../components/pagination/PaginationComponent.js'
import { useAuth0 } from "@auth0/auth0-react";
import { apiGet, apiPut, apiPost } from "../services/AuthService.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpLong, faArrowDownLong, faSort, faFileExcel } from '@fortawesome/free-solid-svg-icons'
import {
    btnGold, boxshadowHover, boxshadowUnHover,
    formLabel, inputSm, goldInputFocus,
    paginationStyle, activePaginationStyle, COLORS
} from '../styles/goldTheme.js'

const CdexCodes_dc = props => {

    //properties
    const baseUrl = process.env.REACT_APP_API_BASEURL;
    const { getAccessTokenSilently } = useAuth0();

    //stateful properties (hooks)
    const params = useParams();
    const [currentAccount, accountProfile, setAccountProfile, toggleIsShowSidebarMd, setIsShowSidebarMd] = useOutletContext();
    const navigate = useNavigate();

    //admin-only global mappings view. accountId comes from props when embedded
    //(AdminDashboard Global Mappings tab) or from the URL param when navigated
    //to standalone (/cdexcodes_dc/69). 69 = TOTData Global Mapping account (Dev).
    const targetAccountId = props.accountId || params.accountId;
    const [pageStatus, setPageStatus] = useState('loading');
    const [dataStatus, setDataStatus] = useState('loading');
    const [pageErrors, setPageErrors] = useState('');
    const [dataErrors, setDataErrors] = useState('');
    const [revLineTypes, setRevLineTypes] = useState([{ code: '', value: '' }, { code: 'R', value: 'REV' }, { code: 'T', value: 'TAX' }, { code: 'D', value: 'DED' }, { code: 'N', value: 'NET' }]);
    const [originalProductCodes, setOriginalProductCodes] = useState([]);
    const [productCodes, setProductCodes] = useState([]);
    const [selectedProductIndex, setSelectedProductIndex] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [anyProductDirty, setAnyProductDirty] = useState(false);
    const [anyTransactionDirty, setAnyTransactionDirty] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(true);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [hoveredTransactionIndex, setHoveredTransactionIndex] = useState(null);
    const [dataCriteria, setDataCriteria] = useState({
        skipCount: 0,
        takeCount: 25
    });
    const [rowCount, setRowCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const companyTopRef = useRef(null);
    const editRef = useRef(null);

    //add-product / add-transaction modal state
    //Note: on the _dc pages ProductCode == ProductDesc and TransactionCode == TransactionDesc,
    //so the modals only collect the desc; the code field is auto-populated on submit.
    const [showAddProductModal, setShowAddProductModal] = useState(false);
    const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
    const [addProductForm, setAddProductForm] = useState({ productDesc: '', cdexProductCode: '' });
    const [addTransactionForm, setAddTransactionForm] = useState({ transactionDesc: '', cdexTransactionCode: '', revLineTypeCode: 'R', applyToAll: false });
    const [modalError, setModalError] = useState('');
    const [modalSubmitting, setModalSubmitting] = useState(false);

    // Filter / sort state for the master pane (Products). Same structure as the
    // override page so the patterns transfer cleanly.
    const [productDescFilter, setProductDescFilter] = useState('');
    const [cdexProductCodeFilter, setCdexProductCodeFilter] = useState('');
    const [productSortBy, setProductSortBy] = useState(null);   // 'productDesc' | 'cdexProductCode' | null
    const [productSortDir, setProductSortDir] = useState(null); // 'asc' | 'desc' | null

    // Filter / sort state for the detail pane (Transactions list for the selected product).
    const [transactionDescFilter, setTransactionDescFilter] = useState('');
    const [cdexTransactionCodeFilter, setCdexTransactionCodeFilter] = useState('');
    const [revLineTypeFilter, setRevLineTypeFilter] = useState('');
    const [transactionSortBy, setTransactionSortBy] = useState(null);
    const [transactionSortDir, setTransactionSortDir] = useState(null);

    // XLS download status — mirrors the override page.
    const [downloadDataStatus, setDownloadDataStatus] = useState('idle');

    //initialize page
    useEffect(() => {
        (async () => {
            //console.log('Initializing page...');

            //skip sidebar toggle when embedded inside AdminDashboard so we don't
            //fight with whatever sidebar state AdminDashboard already set
            if (!props.embedded && setIsShowSidebarMd)
                setIsShowSidebarMd(true);

            await initializeForm()
                .then(initializeResponse => {
                    populateCdexProductCodes(1)
                        .then(response => {
                            setPageStatus('ready')
                        })
                        .catch(ex => {
                            setPageStatus('error');
                            setPageErrors(ex.message);

                            setDataStatus('error');
                            setDataErrors(ex.message);
                        })
                        .finally(response => {
                            //do something
                        })
                })
                .catch(ex => {
                    setPageStatus('error');
                    setPageErrors(ex.message);
                })
                .finally(response => {
                    //do something
                })
        })();
    }, []);

    //initialize form
    const initializeForm = async () => {
        //TODO: might just call populateOperators() from useEffect
        //console.log('Initializing form...');

        return { success: true };

        //TODO: might want to clean this up with .then /.catch, but how to do with multiple api calls?
        //.then(get other data)
        //.catch(ex => throw...)
    };

    //populate productCodes
    //preserveSelectedId (optional): when re-fetching after a save, pass the previously
    //selected product's id so we land back on the same row instead of resetting to index 0
    const populateCdexProductCodes = async (currentPage, preserveSelectedId) => {
        //console.log('Current Page: ' + currentPage);
        
        //note: have to recalcaulate skipCount here since state updates aren't active until re-render
        let skipCount = (currentPage === 1 ? 0 : (currentPage - 1) * dataCriteria.takeCount);
        //console.log('Skip Count: ' + skipCount);

        //setDataStatus('loading');

        const currentCriteria = {
            skipCount: skipCount,
            takeCount: dataCriteria.takeCount
        }
        
        await getCdexProductCodes(currentCriteria)
            .then(response => {
                if (response.data.length === 0) {
                    //console.log(response.data);
                    setHoveredIndex(null);
                    setSelectedProductIndex(null);
                    setSelectedProduct(null);
                    setAnyProductDirty(false);
                    setAnyTransactionDirty(false);
                    setDataStatus('nodata')
                }
                else {
                    //console.log('Company Count: ' + response.data[0].rowCount);
                    //honor preserveSelectedId so refreshes after a save land back on the
                    //same row the user had selected (falls back to 0 if id not found, e.g.
                    //a page-boundary case)
                    let idx = 0;
                    if (preserveSelectedId) {
                        const found = response.data.findIndex(p => p.id === preserveSelectedId);
                        if (found >= 0) idx = found;
                    }
                    setIsReadOnly(true);
                    setHoveredIndex(null);
                    setSelectedProductIndex(idx);
                    setSelectedProduct(response.data[idx]);
                    setAnyProductDirty(false);
                    setAnyTransactionDirty(false);

                    setRowCount(response.rowCount);
                    setOriginalProductCodes(response.data);
                    setProductCodes(response.data);
                    setDataStatus('ready')
                }
            })
            .catch(ex => {
                setDataStatus('error');
                setDataErrors(ex.message);
            })
            .finally(response => {
                //do something
            })
    };

    //get productCodes
    const getCdexProductCodes = async (criteria) => {
        //console.log('Getting CdexProductCodes...');

        const apiUrl = 'accounts/' + targetAccountId + '/cdexproductcodes?skipcount=' + criteria.skipCount + '&takecount=' + criteria.takeCount + '&documentType=DC_REV';
        //console.log(apiUrl);

        const response = await apiGet(apiUrl, getAccessTokenSilently);
        const json = await response.json();
        console.log('Response - Status: ' + response.status + ', Success: ' + response.ok + ', Json: ' + JSON.stringify(json));

        return { success: response.ok, rowCount: json.rowCount, data: json.data };
    };

    //handle product changes
    const handleProductChange = (event) => {
        event.preventDefault();

        const fieldName = event.target.name;
        let value = event.target.value;
        //console.log('ownerId: ' + ownerId, 'Field: ' + fieldName + ', Value: ' + value);

        if (fieldName == 'cdexProductCode') {
            if (value.length > 2) {
                value = value.substring(0, 2);
            }
        }

        let anyProductDirty = false;
        let anyProductInvalid = false;
        const newProductList = [];
        productCodes.map((product, index) => {
            if (index === selectedProductIndex) {
                const originalProduct = originalProductCodes[index];
                const originalValue = originalProduct.cdexProductCode;

                const isDirty = (value !== originalValue ? true : false);
                const isValid = (value !== '' ? true : false);

                newProductList.push({
                    ...product,
                    [fieldName]: value,
                    isDirty: isDirty,
                    isValid: isValid
                });

                if (isDirty && isValid)
                    anyProductDirty = true;

                if (isDirty && !isValid)
                    anyProductInvalid = true;

                //console.log('Updating product: ' + product.productCode);
            }
            else {
                newProductList.push(product);

                if (product.isDirty && product.isValid)
                    anyProductDirty = true;

                if (product.isDirty && !product.isValid)
                    anyProductInvalid = true;
            }            
        });
        setProductCodes(newProductList);

        setAnyProductDirty(anyProductDirty && !anyProductInvalid);

        newProductList.map(product => {
            console.log('New Product: ' + product.productCode + ', ' + product.productDesc + ', ' + product.cdexProductCode + ', ' + product.isDirty);
        });
    }

    //handle transaction changes
    const handleTransactionChange = (currentIndex, event) => {
        event.preventDefault();

        const fieldName = event.target.name;
        let value = event.target.value;
        //console.log('Current Index: ' + currentIndex + ', Field: ' + fieldName + ', Value: ' + value);

        if (fieldName == 'cdexTransactionCode') {
            if (value.length > 3) {
                value = value.substring(0, 3);
            }
        }

        let anyTransactionDirty = false;
        let anyTransactionInvalid = false;        
        const newProductList = [];
        const newTransactionList = [];
        productCodes.map((product, productIndex) => {
            const originalProduct = originalProductCodes[productIndex];

            if (productIndex === selectedProductIndex) {

                product.cdexTransactionRowDTO.data.map((transaction, transactionIndex) => {

                    if (transactionIndex === currentIndex) {
                        const originalTransaction = originalProduct.cdexTransactionRowDTO.data[transactionIndex];
                        const originalCdexTranCode = originalTransaction.cdexTransactionCode;
                        const originalRevLineTypeCode = originalTransaction.revLineTypeCode;

                        const cdexTranCodeValue = fieldName === 'cdexTransactionCode' ? value : transaction.cdexTransactionCode;
                        const revLineTypeCode = fieldName === 'revLineTypeCode' ? value : transaction.revLineTypeCode;
                        const isDirty = (cdexTranCodeValue !== originalCdexTranCode || revLineTypeCode !== originalRevLineTypeCode);
                        const isValid = (cdexTranCodeValue !== '' && revLineTypeCode !== '') || (cdexTranCodeValue === '' && revLineTypeCode === '');

                        newTransactionList.push({
                            ...transaction,
                            [fieldName]: value,
                            isDirty: isDirty,
                            isValid: isValid
                        });

                        if (isDirty && isValid)
                            anyTransactionDirty = true;

                        if (isDirty && !isValid)
                            anyTransactionInvalid = true;
                    }
                    else {
                        newTransactionList.push(transaction);

                        if (transaction.isDirty && transaction.isValid)
                            anyTransactionDirty = true;

                        if (transaction.isDirty && !transaction.isValid)
                            anyTransactionInvalid = true;
                    }
                });

                newProductList.push({
                    ...product,
                    cdexTransactionRowDTO: { ...product.cdexTransactionRowDTO, data: newTransactionList }
                });

                //console.log('Updating transaction: ' + transaction.transactionCode);
            }
            else {
                newProductList.push(product);
            }
        });
        setProductCodes(newProductList);
        setSelectedProduct(newProductList[selectedProductIndex]);
        setAnyTransactionDirty(anyTransactionDirty && !anyTransactionInvalid);

        newProductList.map((product, productIndex) => {
            console.log('New Product: ' + product.productCode + ', ' + product.productDesc + ', ' + product.cdexProductCode + ', ' + product.isDirty);

            if (productIndex === selectedProductIndex) {
                product.cdexTransactionRowDTO.data.map(transaction => {
                    console.log('New Transaction: ' + transaction.transactionCode + ', ' + transaction.transactionDesc + ', ' + transaction.cdexTransactionCode + ', ' + transaction.revLineTypeCode + ', ' + transaction.isDirty + ', ' + transaction.isValid);
                });
            }
        });
    }

    //update products
    const updateProducts = async () => {
        console.log('Updating Products...');
        setDataStatus('loading');

        //disable Map buttons while mapping
        const tranDirtyState = anyTransactionDirty;
        setAnyTransactionDirty(false);
        setAnyProductDirty(false);

        //console.log('Id: ' + selectedProduct.id + ', Product Code: ' + selectedProduct.productCode);

        await saveProducts()
            .then(response => {
                if (response.success) {
                    setAnyProductDirty(false);
                    setOriginalProductCodes(response.data);
                    setProductCodes(response.data);
                    setSelectedProduct(response.data[selectedProductIndex]);
                    setAnyTransactionDirty(tranDirtyState);
                    setDataStatus('ready')
                }
                else {
                    //TODO: figure out what to do here
                    console.log('nodata');
                    //setDataStatus('nodata')
                }
            })
            .catch(ex => {
                setDataStatus('error');
                setDataErrors(ex.message);
            })
            .finally(response => {
                //do something
            })
    };

    //save products
    const saveProducts = async () => {
        //console.log('Saving Products...');

        const apiUrl = 'accounts/' + targetAccountId + '/updateproductcodes?documentType=DC_REV';
        const jsonBody = JSON.stringify(productCodes);
        //console.log(apiUrl);
        //console.log(jsonBody);

        const response = await apiPut(apiUrl, jsonBody, getAccessTokenSilently);
        const json = await response.json();

        return { success: response.ok, data: json };
    };

    //update transaction (apply-to-all aware). Same flow as the override page:
    //  1. Propagate any applyToAll changes to other products with matching TransactionDesc
    //  2. Collect every product with any dirty transaction
    //  3. Send them all in one bulk call so the operation is atomic
    const updateTransactions = async () => {
        console.log('Updating Transactions...');
        setDataStatus('loading');

        const prodDirtyState = anyProductDirty;
        setAnyTransactionDirty(false);
        setAnyProductDirty(false);

        //Step 1: propagate applyToAll changes
        const propagatedProducts = applyAllPropagation(productCodes);

        //Step 2: build list of products to save (any with isDirty transactions)
        const productsToSave = propagatedProducts
            .map((p, idx) => ({ product: p, index: idx }))
            .filter(({ product }) =>
                product.cdexTransactionRowDTO
                && product.cdexTransactionRowDTO.data
                && product.cdexTransactionRowDTO.data.some(t => t.isDirty)
            );

        if (productsToSave.length === 0) {
            setAnyTransactionDirty(false);
            setAnyProductDirty(prodDirtyState);
            setDataStatus('ready');
            return;
        }

        try {
            //Step 3: one bulk call — backend wraps in a DB transaction
            const response = await saveProductsBulk(productsToSave.map(({ product }) => product));
            if (!response.success) {
                throw new Error('Bulk save failed.');
            }

            const newProductList = [...propagatedProducts];
            const savedData = Array.isArray(response.data) ? response.data : [];
            productsToSave.forEach(({ index }, i) => {
                if (savedData[i]) newProductList[index] = savedData[i];
            });

            setOriginalProductCodes(newProductList);
            setProductCodes(newProductList);
            setSelectedProduct(newProductList[selectedProductIndex]);
            setAnyProductDirty(prodDirtyState);
            setAnyTransactionDirty(false);
            setDataStatus('ready');
        } catch (ex) {
            console.log('Error during transactions save: ' + ex.message);
            setDataStatus('error');
            setDataErrors(ex.message);
        }
    };

    //save transactions (legacy single-product wrapper, kept in case anything else
    //still calls it). Routes through the bulk endpoint with a single-element array.
    const saveTransactions = async () => {
        const response = await saveProductsBulk([selectedProduct]);
        return {
            success: response.success,
            data: Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null
        };
    };

    //open the Add Product Code modal — reset form and any prior error
    const handleAddProductCode = () => {
        setAddProductForm({ productDesc: '', cdexProductCode: '' });
        setModalError('');
        setShowAddProductModal(true);
    };

    //open the Add Transaction Code modal — reset form, default to "selected product only"
    const handleAddTransactionCode = () => {
        setAddTransactionForm({ transactionDesc: '', cdexTransactionCode: '', revLineTypeCode: 'R', applyToAll: false });
        setModalError('');
        setShowAddTransactionModal(true);
    };

    //submit the Add Product Code modal — POSTs to backend, on success appends new row
    const submitAddProductCode = async () => {
        //quick required-field validation
        if (!addProductForm.productDesc || !addProductForm.cdexProductCode) {
            setModalError('All fields are required.');
            return;
        }

        setModalSubmitting(true);
        setModalError('');
        try {
            const apiUrl = 'accounts/' + targetAccountId + '/AddCdexProductCode?documentType=DC_REV';
            //ProductCode = ProductDesc on the _dc pages (per the page's design rule)
            const body = JSON.stringify({
                productCode: addProductForm.productDesc,
                productDesc: addProductForm.productDesc,
                cdexProductCode: addProductForm.cdexProductCode
            });
            const response = await apiPost(apiUrl, body, true, getAccessTokenSilently);
            if (!response.ok) {
                const errText = await response.text();
                setModalError(errText || 'Failed to add product code.');
                return;
            }
            const newProduct = await response.json();
            //insert at the correct alphabetical position so the row matches the
            //backend's sort order (avoids a flash of "new row at the bottom" before
            //next re-fetch)
            const newDesc = (newProduct.productDesc || '').toLowerCase();
            const insertAt = productCodes.findIndex(p => (p.productDesc || '').toLowerCase() > newDesc);
            const insertIdx = insertAt === -1 ? productCodes.length : insertAt;
            const newList = [
                ...productCodes.slice(0, insertIdx),
                newProduct,
                ...productCodes.slice(insertIdx)
            ];
            setProductCodes(newList);
            setOriginalProductCodes(newList);
            setSelectedProductIndex(insertIdx);
            setSelectedProduct(newProduct);
            setShowAddProductModal(false);
        } catch (ex) {
            setModalError(ex.message || 'Unexpected error.');
        } finally {
            setModalSubmitting(false);
        }
    };

    //submit the Add Transaction Code modal — POSTs to backend
    //if applyToAll is true, adds the same transaction to every product in this account+docType
    //if false, adds only to the currently selected product
    const submitAddTransactionCode = async () => {
        if (!addTransactionForm.transactionDesc || !addTransactionForm.cdexTransactionCode || !addTransactionForm.revLineTypeCode) {
            setModalError('All fields are required.');
            return;
        }
        if (!addTransactionForm.applyToAll && (!selectedProduct || !selectedProduct.id)) {
            setModalError('No product is currently selected. Choose a product or check "Apply to all".');
            return;
        }

        setModalSubmitting(true);
        setModalError('');
        try {
            const productParam = addTransactionForm.applyToAll
                ? '&applyToAll=true'
                : '&productCodeId=' + selectedProduct.id;
            const apiUrl = 'accounts/' + targetAccountId + '/AddCdexTransactionCode?documentType=DC_REV' + productParam;
            //TransactionCode = TransactionDesc on the _dc pages (per the page's design rule)
            const body = JSON.stringify({
                transactionCode: addTransactionForm.transactionDesc,
                transactionDesc: addTransactionForm.transactionDesc,
                cdexTransactionCode: addTransactionForm.cdexTransactionCode,
                revLineTypeCode: addTransactionForm.revLineTypeCode
            });
            const response = await apiPost(apiUrl, body, true, getAccessTokenSilently);
            if (!response.ok) {
                const errText = await response.text();
                setModalError(errText || 'Failed to add transaction code.');
                return;
            }
            //easiest correct refresh: re-fetch the page so transaction lists rebuild
            //(applyToAll touches many products, so a targeted state update gets messy).
            //Preserve the previously selected product so we don't snap back to "ALL" (or
            //whichever row sorts first alphabetically) after the save.
            const prevSelectedId = selectedProduct ? selectedProduct.id : null;
            await populateCdexProductCodes(currentPage, prevSelectedId);
            setShowAddTransactionModal(false);
        } catch (ex) {
            setModalError(ex.message || 'Unexpected error.');
        } finally {
            setModalSubmitting(false);
        }
    };

    //handle mouse over
    const handleMouseOver = async (index) => {
        console.log('Mouse Over: ' + index);

        if (index != selectedProductIndex) {
            setHoveredIndex(index);
        }
    };

    //handle mouse leave
    const handleMouseLeave = async (index) => {
        console.log('Mouse Leave: ' + index);

        if (index != selectedProductIndex) {
            setHoveredIndex(null);
        }
    };

    //handle mouse click
    const handleMouseClick = async (index) => {
        console.log('Mouse Click: ' + index);

        setHoveredIndex(null);
        setSelectedProductIndex(index);
        setSelectedProduct(productCodes[index]);

        let anyTransactionDirty = false;
        let anyTransactionInvalid = false;
        productCodes[index].cdexTransactionRowDTO.data.map(transaction => {
            if (transaction.isDirty && transaction.isValid) {
                anyTransactionDirty = true;
            }

            if (transaction.isDirty && !transaction.isValid) {
                anyTransactionInvalid = true;
            }
        });
        setAnyTransactionDirty(anyTransactionDirty && !anyTransactionInvalid);
    };

    //handle page click
    const handlePageClick = async (currentPage) => {
        //console.log('Current Page: ' + currentPage);

        //setDataStatus('loading');

        setDataCriteria({
            ...dataCriteria,
            skipCount: 0
        });

        //don't need to do here, since pagination component sets before calling this method
        //setCurrentPage(1);

        await populateCdexProductCodes(currentPage);
    };

    //----------------------------------------------------------------
    // Filter / sort helpers (parallel to CdexCodesOverride_dc.js).
    //----------------------------------------------------------------

    // Sort cycle (off → asc → desc → off) for columns with no natural order.
    const cycleProductSort = (column) => {
        if (productSortBy !== column) { setProductSortBy(column); setProductSortDir('asc'); }
        else if (productSortDir === 'asc') { setProductSortDir('desc'); }
        else { setProductSortBy(null); setProductSortDir(null); }
    };
    const cycleTransactionSort = (column) => {
        if (transactionSortBy !== column) { setTransactionSortBy(column); setTransactionSortDir('asc'); }
        else if (transactionSortDir === 'asc') { setTransactionSortDir('desc'); }
        else { setTransactionSortBy(null); setTransactionSortDir(null); }
    };

    // Toggle cycle (off → desc → off) for columns whose data is already sorted asc by
    // the backend OrderBy (Product Desc and Interest Type Desc). First click reverses
    // the default; second click restores it.
    const cycleProductSortFromAsc = (column) => {
        if (productSortBy === column && productSortDir === 'desc') {
            setProductSortBy(null); setProductSortDir(null);
        } else {
            setProductSortBy(column); setProductSortDir('desc');
        }
    };
    const cycleTransactionSortFromAsc = (column) => {
        if (transactionSortBy === column && transactionSortDir === 'desc') {
            setTransactionSortBy(null); setTransactionSortDir(null);
        } else {
            setTransactionSortBy(column); setTransactionSortDir('desc');
        }
    };

    const sortIconFor = (activeColumn, activeDir, column, opts) => {
        if (activeColumn === column) {
            return activeDir === 'asc' ? faArrowUpLong : faArrowDownLong;
        }
        if (opts && opts.defaultAsc) return faArrowUpLong;
        return faSort;
    };

    const cmpStr = (a, b) => {
        const av = (a == null ? '' : String(a)).toLowerCase();
        const bv = (b == null ? '' : String(b)).toLowerCase();
        return av < bv ? -1 : av > bv ? 1 : 0;
    };

    const revLineTypeLabel = (code) => {
        switch (code) {
            case 'R': return 'REV';
            case 'T': return 'TAX';
            case 'D': return 'DED';
            case 'N': return 'NET';
            default: return '';
        }
    };

    // Shared filter input/select/icon styles. minHeight is critical to defeat
    // Bootstrap's .form-control-sm/.form-select-sm min-height; we also set this
    // via a !important style block on the <li className="dc-filter-row"> below
    // so Bootstrap can't push the rendered height past 24px.
    const filterInputStyle = { fontSize: '.8rem', paddingTop: '0px', paddingBottom: '0px', paddingLeft: '6px', paddingRight: '6px', height: '24px', minHeight: '24px', lineHeight: 1.2, boxSizing: 'border-box' };
    const filterSelectStyle = { fontSize: '.8rem', paddingTop: '0px', paddingBottom: '0px', paddingLeft: '6px', paddingRight: '22px', height: '24px', minHeight: '24px', lineHeight: 1.2, backgroundPosition: 'right 4px center', boxSizing: 'border-box', flex: 1 };
    const filterSortIconStyle = (active) => ({ cursor: 'pointer', width: '12px', height: '12px', color: active ? COLORS.gold : '#9ca3af', flexShrink: 0 });
    const filterRowLiStyle = { paddingLeft: '20px', marginLeft: '-42px', marginRight: '0px', fontSize: props.embedded ? '.75rem' : '.85rem', position: 'sticky', top: 0, zIndex: 2, background: '#ffffff', borderBottom: '1px solid #e5e7eb', paddingTop: '4px', paddingBottom: '4px' };

    // Master products — filtered + sorted view. Mid-edit bypass (isDirty) keeps
    // a row visible while the user is editing its CdexProductCode so it doesn't
    // vanish mid-keystroke.
    const visibleProducts = useMemo(() => {
        let list = (productCodes || []).map((p, originalIndex) => ({ ...p, _originalIndex: originalIndex }));
        if (productDescFilter) {
            const q = productDescFilter.toLowerCase();
            list = list.filter(p => (p.productDesc || '').toLowerCase().includes(q));
        }
        if (cdexProductCodeFilter !== '') {
            list = list.filter(p =>
                (p.cdexProductCode || '') === cdexProductCodeFilter
                || p.isDirty === true
            );
        }
        if (productSortBy && productSortDir) {
            list = [...list].sort((a, b) => {
                const r = cmpStr(a[productSortBy], b[productSortBy]);
                return productSortDir === 'asc' ? r : -r;
            });
        }
        return list;
    }, [productCodes, productDescFilter, cdexProductCodeFilter, productSortBy, productSortDir]);

    // Keep the selected product in sync with what's currently visible. When the
    // master-pane filters narrow visibleProducts so the currently selected product
    // is no longer in the list, snap selection to the first visible row — otherwise
    // the detail pane keeps showing transactions for a product the user can't see,
    // which produces "blank transaction" rows in the XLS export and generally
    // confuses the user about what they're looking at.
    useEffect(() => {
        if (!visibleProducts || visibleProducts.length === 0) return;
        if (selectedProductIndex == null) return;

        const stillVisible = visibleProducts.some(p => p._originalIndex === selectedProductIndex);
        if (stillVisible) return;

        const firstVisible = visibleProducts[0];
        setSelectedProductIndex(firstVisible._originalIndex);
        setSelectedProduct(productCodes[firstVisible._originalIndex]);
    }, [visibleProducts]);

    const distinctCdexProductCodes = useMemo(() => {
        const set = new Set();
        (productCodes || []).forEach(p => set.add(p.cdexProductCode || ''));
        return Array.from(set).sort();
    }, [productCodes]);

    const visibleTransactions = useMemo(() => {
        const source = (selectedProduct && selectedProduct.cdexTransactionRowDTO && selectedProduct.cdexTransactionRowDTO.data) || [];
        let list = source.map((t, originalIndex) => ({ ...t, _originalIndex: originalIndex }));
        if (transactionDescFilter) {
            const q = transactionDescFilter.toLowerCase();
            list = list.filter(t => (t.transactionDesc || '').toLowerCase().includes(q));
        }
        if (cdexTransactionCodeFilter !== '') {
            list = list.filter(t =>
                (t.cdexTransactionCode || '') === cdexTransactionCodeFilter
                || t.isDirty === true
            );
        }
        if (revLineTypeFilter !== '') {
            list = list.filter(t =>
                (t.revLineTypeCode || '') === revLineTypeFilter
                || t.isDirty === true
            );
        }
        if (transactionSortBy && transactionSortDir) {
            list = [...list].sort((a, b) => {
                const r = cmpStr(a[transactionSortBy], b[transactionSortBy]);
                return transactionSortDir === 'asc' ? r : -r;
            });
        }
        return list;
    }, [selectedProduct, transactionDescFilter, cdexTransactionCodeFilter, revLineTypeFilter, transactionSortBy, transactionSortDir]);

    const distinctCdexTransactionCodes = useMemo(() => {
        const data = (selectedProduct && selectedProduct.cdexTransactionRowDTO && selectedProduct.cdexTransactionRowDTO.data) || [];
        const set = new Set();
        data.forEach(t => set.add(t.cdexTransactionCode || ''));
        return Array.from(set).sort();
    }, [selectedProduct]);

    const distinctRevLineTypeCodes = useMemo(() => {
        const data = (selectedProduct && selectedProduct.cdexTransactionRowDTO && selectedProduct.cdexTransactionRowDTO.data) || [];
        const set = new Set();
        data.forEach(t => set.add(t.revLineTypeCode || ''));
        return Array.from(set).sort();
    }, [selectedProduct]);

    // Toggle the "apply to all products" flag on a single transaction row in the
    // selected product. Save-time directive only — doesn't touch isDirty/isValid.
    const handleTransactionApplyToAllChange = (currentIndex, checked) => {
        const newProductList = productCodes.map((product, productIndex) => {
            if (productIndex !== selectedProductIndex) return product;
            const newTransactions = product.cdexTransactionRowDTO.data.map((t, i) =>
                i === currentIndex ? { ...t, applyToAll: checked } : t
            );
            return {
                ...product,
                cdexTransactionRowDTO: { ...product.cdexTransactionRowDTO, data: newTransactions }
            };
        });
        setProductCodes(newProductList);
        setSelectedProduct(newProductList[selectedProductIndex]);
    };

    // Apply-to-all propagation. Walks the selected product's transactions, collects
    // every applyToAll+isDirty transaction, and overlays the new values onto matching
    // (same TransactionDesc) transactions in other products. Returns a new productCodes
    // array — the caller decides whether to commit.
    const applyAllPropagation = (products) => {
        const selProd = products[selectedProductIndex];
        if (!selProd || !selProd.cdexTransactionRowDTO || !selProd.cdexTransactionRowDTO.data) return products;

        const propagations = new Map();
        selProd.cdexTransactionRowDTO.data.forEach(t => {
            if (t.applyToAll && t.isDirty && t.transactionDesc) {
                propagations.set(t.transactionDesc.toLowerCase(), {
                    cdexTransactionCode: t.cdexTransactionCode,
                    revLineTypeCode: t.revLineTypeCode
                });
            }
        });
        if (propagations.size === 0) return products;

        return products.map((product, productIndex) => {
            if (productIndex === selectedProductIndex) return product;
            if (!product.cdexTransactionRowDTO || !product.cdexTransactionRowDTO.data) return product;

            let changed = false;
            const newTransactions = product.cdexTransactionRowDTO.data.map(t => {
                const prop = propagations.get((t.transactionDesc || '').toLowerCase());
                if (!prop) return t;
                if (t.cdexTransactionCode === prop.cdexTransactionCode && t.revLineTypeCode === prop.revLineTypeCode) return t;
                changed = true;
                return { ...t, cdexTransactionCode: prop.cdexTransactionCode, revLineTypeCode: prop.revLineTypeCode, isDirty: true, isValid: true };
            });
            if (!changed) return product;
            return { ...product, cdexTransactionRowDTO: { ...product.cdexTransactionRowDTO, data: newTransactions } };
        });
    };

    // Bulk-save a list of products through the new UpdateTransactionCodesBulk
    // endpoint (parallel to OverrideTransactionCodesBulk on the override page).
    const saveProductsBulk = async (products) => {
        const apiUrl = 'accounts/' + targetAccountId + '/updatetransactioncodesbulk?documentType=DC_REV';
        const jsonBody = JSON.stringify(products);
        const response = await apiPut(apiUrl, jsonBody, getAccessTokenSilently);
        const json = await response.json();
        return { success: response.ok, data: json };
    };

    // Build the flat export rows from the current filtered/sorted view. For the
    // global page Source is always blank (no override/inherited distinction here);
    // we still send the field so the backend's existing row shape doesn't change.
    const buildExportRows = () => {
        const rows = [];
        visibleProducts.forEach(product => {
            const allTx = (product.cdexTransactionRowDTO && product.cdexTransactionRowDTO.data) || [];
            let visibleTx = allTx;
            if (transactionDescFilter) {
                const q = transactionDescFilter.toLowerCase();
                visibleTx = visibleTx.filter(t => (t.transactionDesc || '').toLowerCase().includes(q));
            }
            if (cdexTransactionCodeFilter !== '') {
                visibleTx = visibleTx.filter(t => (t.cdexTransactionCode || '') === cdexTransactionCodeFilter);
            }
            if (revLineTypeFilter !== '') {
                visibleTx = visibleTx.filter(t => (t.revLineTypeCode || '') === revLineTypeFilter);
            }
            if (transactionSortBy && transactionSortDir) {
                visibleTx = [...visibleTx].sort((a, b) => {
                    const r = cmpStr(a[transactionSortBy], b[transactionSortBy]);
                    return transactionSortDir === 'asc' ? r : -r;
                });
            }
            if (visibleTx.length === 0) {
                //When a transaction filter is active, a product with no matching
                //transactions shouldn't appear at all — emitting an empty-transaction
                //row would clutter the export with blank columns. Only push the
                //"product exists but has no transactions" fallback when there's no
                //transaction filter applied.
                const anyTxFilterActive =
                    !!transactionDescFilter
                    || cdexTransactionCodeFilter !== ''
                    || revLineTypeFilter !== '';
                if (anyTxFilterActive) return;

                rows.push({
                    productDesc: product.productDesc || '',
                    cdexProductCode: product.cdexProductCode || '',
                    transactionDesc: '',
                    cdexTransactionCode: '',
                    revLineTypeCode: '',
                    source: 'GLOBAL'
                });
                return;
            }
            visibleTx.forEach(t => {
                rows.push({
                    productDesc: product.productDesc || '',
                    cdexProductCode: product.cdexProductCode || '',
                    transactionDesc: t.transactionDesc || '',
                    cdexTransactionCode: t.cdexTransactionCode || '',
                    //Use the human-friendly label (REV/TAX/DED/NET) instead of the raw
                    //single-letter code so the Excel matches what the UI shows.
                    revLineTypeCode: revLineTypeLabel(t.revLineTypeCode) || (t.revLineTypeCode || ''),
                    source: 'GLOBAL'
                });
            });
        });
        return rows;
    };

    // Download the current view as XLSX. Reuses the override page's endpoint —
    // the backend just turns whatever rows it receives into a workbook, so the
    // global page works the same way (Source column will be blank).
    const handleExportXls = async () => {
        if (!targetAccountId) return;
        setDownloadDataStatus('loading');
        try {
            const exportRows = buildExportRows();
            if (exportRows.length === 0) {
                setDownloadDataStatus('idle');
                console.log('No rows match the current filters — nothing to export.');
                return;
            }
            const apiUrl = 'accounts/' + targetAccountId + '/cdexoverrideexcel';
            const fetchResponse = await apiPost(apiUrl, JSON.stringify(exportRows), true, getAccessTokenSilently);
            if (!fetchResponse.ok) {
                throw new Error('XLS export failed (HTTP ' + fetchResponse.status + ').');
            }
            const blob = await fetchResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'CDEXGlobalMappings.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setDownloadDataStatus('ready');
        } catch (err) {
            console.error('Error exporting CDEX Global Mappings XLSX:', err);
            setDownloadDataStatus('error');
        }
    };

    //render page loading
    //note: spinner overlay will eventually be at the page level (I think)
    const showPageLoading = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <Spinner animation="border" style={{ color: COLORS.gold }} />
            </div>
        );
    }

    //render page loading
    const showDataLoading = () => {
        return (
            <Row style={{ justifyContent: 'center', padding: '40px 0' }}>
                <Spinner animation="border" style={{ color: COLORS.gold, width: 'auto' }} />
            </Row>
        );
    }

    //render page error
    const showPageError = () => {
        return (
            <div>
                Oops, there was a page load error: {pageErrors}
            </div>
        );
    }

    //render data error
    const showDataError = () => {
        return (
            <Row>
                Oops, there was a data load error: {dataErrors}
            </Row>
        );
    }

    //render table container
    const showTableContainer = () => {
        return (
            <Card className="mb-4" style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 14px rgba(0,0,0,.10)' }}>
                <Card.Header style={{ background: '#333333', color: '#f1f1f1', borderBottom: 'none', padding: props.embedded ? '8px 12px' : '12px 16px' }}>
                    {showInputCriteria()}
                </Card.Header>
                <Card.Body style={{paddingLeft: '12px', paddingRight: '10px'}} >
                    {(() => {
                        switch (dataStatus) {
                            case 'nodata':
                                return <p>No Data Loaded.</p>
                            case 'loading':
                                return showDataLoading()
                            case 'ready':
                                return showData()
                            case 'error':
                                return showDataError()
                            default:
                                return <Row>No Data Loaded.</Row>
                        }
                    })()}
                </Card.Body>
            </Card>
        );
    }

    //render productCodes
    //Note: no `overflow: auto` on the Cols — the inner <ul>'s now have their own
    //overflowY: auto for the sticky filter row. Double scrollbars are gone.
    const showData = () => {
        return (
            <Row>
                <Col xs={5} style={{ marginTop: '-16px', marginBottom: '-16px', paddingRight: '0px', paddingLeft: '0px', borderRight: '1px solid #e5e7eb' }} >
                {(() => {
                    switch (dataStatus) {
                        case 'ready':
                            return showDataMaster()
                        default:
                            return null
                    }
                    })()}
                </Col>
                <Col xs={7} style={{ height: props.embedded ? '405px' : '530px', paddingLeft: '20px', marginTop: '-10px', marginBottom: '-16px' }}>
                    {(() => {
                        switch (dataStatus) {
                            case 'ready':
                                return showDataDetail()
                            default:
                                return null
                        }
                    })()}
                </Col>
            </Row>
        );
    }

    //render master pane (productCodes list)
    const showDataMaster = () => {
        return (
            <>
                <ul style={{ listStyleType: "none", height: props.embedded ? "390px" : "515px", marginRight: '12px', overflowY: 'auto', overflowX: 'hidden' }}>
                    {/* Sticky filter / sort row — first <li> in the <ul> so it shares the same
                        horizontal offset (negative marginLeft) and aligns with the data
                        columns. position: sticky keeps it pinned as the list scrolls. */}
                    <li className="dc-filter-row" style={filterRowLiStyle}>
                        <Row>
                            {/* Product Desc — search box + sort cycle (data is naturally asc) */}
                            <Col xs={7} style={{ paddingRight: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder="Search..."
                                        value={productDescFilter}
                                        onChange={(e) => setProductDescFilter(e.target.value)}
                                        style={{ ...filterInputStyle, flex: 1 }}
                                    />
                                    <FontAwesomeIcon
                                        icon={sortIconFor(productSortBy, productSortDir, 'productDesc', { defaultAsc: true })}
                                        title={productSortBy === 'productDesc' && productSortDir === 'desc' ? 'Sorted descending — click to restore default (ascending)' : 'Currently ascending — click to sort descending'}
                                        style={filterSortIconStyle(productSortBy === 'productDesc')}
                                        onClick={() => cycleProductSortFromAsc('productDesc')}
                                    />
                                </div>
                            </Col>

                            {/* CDEX Product Code — dropdown + sort cycle */}
                            <Col xs={5}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Form.Select
                                        size="sm"
                                        value={cdexProductCodeFilter}
                                        onChange={(e) => setCdexProductCodeFilter(e.target.value)}
                                        style={filterSelectStyle}
                                    >
                                        <option value="">All</option>
                                        {distinctCdexProductCodes.map((code, i) => (
                                            <option key={i} value={code}>{code === '' ? '(blank)' : code}</option>
                                        ))}
                                    </Form.Select>
                                    <FontAwesomeIcon
                                        icon={sortIconFor(productSortBy, productSortDir, 'cdexProductCode')}
                                        title={productSortBy === 'cdexProductCode' ? (productSortDir === 'asc' ? 'Sorted ascending — click for descending' : 'Sorted descending — click to clear') : 'Click to sort ascending'}
                                        style={filterSortIconStyle(productSortBy === 'cdexProductCode')}
                                        onClick={() => cycleProductSort('cdexProductCode')}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </li>

                    {visibleProducts.length === 0 ? (
                        <li style={{ paddingLeft: '20px', marginLeft: '-42px', color: '#9ca3af', fontStyle: 'italic', fontSize: props.embedded ? '.8rem' : '.9rem' }}>
                            No products match the current filters.
                        </li>
                    ) : null}
                    {visibleProducts.map((cdexProductCode) => {
                        const index = cdexProductCode._originalIndex;
                        return (
                        <li key={index} ref={(index == 0 ? companyTopRef : null)}
                            style={{ cursor: 'pointer', paddingLeft: '20px', marginLeft: '-42px', marginRight: '0px', fontSize: props.embedded ? '.8rem' : '.9rem' }}
                            onMouseOver={() => handleMouseOver(index)}
                            onMouseLeave={() => handleMouseLeave(index)}
                            onClick={() => handleMouseClick(index)}>
                            <Row style={{
                                position: 'relative',
                                borderTop: hoveredIndex == index ? `2px solid ${COLORS.gold}` : '2px solid transparent',
                                borderBottom: hoveredIndex == index ? `2px solid ${COLORS.gold}` : '1px solid #e5e7eb',
                                backgroundColor: (selectedProductIndex == index ? '#f5f0e8' : (index % 2 === 0 ? '#fafbfc' : 'transparent')),
                                color: selectedProductIndex == index ? '#1f2937' : '#374151',
                                fontWeight: selectedProductIndex == index ? 600 : 400,
                                borderLeft: selectedProductIndex == index ? `8px solid ${COLORS.gold}` : '8px solid transparent',
                                paddingTop: '4px',
                                paddingBottom: '4px',
                                transition: 'border-color 120ms ease, background 120ms ease, color 120ms ease, transform 120ms ease, box-shadow 120ms ease',
                                ...(selectedProductIndex == index ? {
                                    boxShadow: '0 3px 8px rgba(0,0,0,0.18)',
                                    transform: 'translateX(3px)',
                                    zIndex: 2,
                                } : {}),
                            }} >
                                {/* Product Code column hidden for _dc — ProductCode same as ProductDesc */}
                                <Col xs={7} style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{
                                        fontSize: props.embedded ? '.9rem' : '1.05rem',
                                        fontWeight: selectedProductIndex == index ? 700 : 600,
                                        letterSpacing: '.01em',
                                    }}>{cdexProductCode.productDesc}</div>
                                </Col>
                                <Col xs={5} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        {(() => {
                                        if (!cdexProductCode.isReadOnly) {
                                                return <Form.Group className={props.embedded ? "mb-0" : "mb-3"} controlId="cdexProductCode">
                                                    <Form.Control name="cdexProductCode" size="sm" aria-label="CDEX Product Code" value={cdexProductCode.cdexProductCode} maxLength={2} style={props.embedded ? { width: '54px', fontSize: '.78rem', padding: '0px 6px', height: '22px', lineHeight: 1.2, textAlign: 'center', fontWeight: 600 } : { width: '64px', textAlign: 'center', fontWeight: 600 }} onChange={e => handleProductChange(e)} />
                                                        </Form.Group>
                                            }
                                            else {
                                                return <div>{cdexProductCode.cdexProductCode}</div>;
                                            }
                                        })()}
                                </Col>
                            </Row>
                        </li>
                        );
                    })}
                </ul>
            </>
        );
    }

    //render detail pane (company display or edit)
    const showDataDetail = () => {
        return (
            <>
                {(() => {
                    if (isReadOnly) {
                        //return showDataDisplay()
                        return showDataEdit()
                    }
                    else {
                        //return showDataEdit()
                        return showDataDisplay()
                    }
                })()}
            </>
        );
    }

    //render company detail
    const showDataDisplay = () => {
        return (
            <>
                <Row>
                    <Col xs={8}>
                        <div style={{ fontWeight: '600', fontSize: '1.25rem', marginTop: '10px', color: '#1f2937' }} >Sender Detail</div>
                    </Col>
                    <Col xs={4} className="d-flex justify-content-end" style={{marginTop: '13px', color: '#374151'}} >
                        Company Code:&nbsp;&nbsp;{selectedProduct.companyCode}&nbsp;&nbsp;&nbsp;&nbsp;
                    </Col>
                </Row>
                <Form style={{ borderTop: '1px solid #e5e7eb', marginTop: '10px', paddingRight: '10px' }} />
                <Row style={{ paddingRight: '10px', paddingTop: '10px' }}>
                    <Col xs={2}>
                        Name:
                    </Col>
                    <Col xs={10}>
                        <div>
                            {selectedProduct.companyName}
                        </div>                        
                        <div>
                            {((selectedProduct.additionalNameInfo ?? '') == '' ? null : '(' + selectedProduct.additionalNameInfo + ')')}
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col xs={2}>
                        Address:
                    </Col>
                    <Col xs={10}>
                        <div>
                            {selectedProduct.address1} {selectedProduct.address2} {selectedProduct.address3}
                        </div>
                        <div>
                            {selectedProduct.city}, {selectedProduct.state} {selectedProduct.zip}
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col xs={2}>
                        Email:
                    </Col>
                    <Col xs={10}>
                        <div style={{ color: ((selectedProduct.email ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.email ?? '') == '' ? 'Not available' : selectedProduct.email}</div>
                    </Col>
                </Row>
                <Row>
                    <Col xs={2}>
                        Phone:
                    </Col>
                    <Col xs={10}>
                        <div style={{ color: ((selectedProduct.phone ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.phone ?? '') == '' ? 'Not available' : selectedProduct.phone}</div>
                    </Col>
                </Row>
                <Row>
                    <Col xs={2}>
                        Fax:
                    </Col>
                    <Col xs={10}>
                        <div style={{ color: ((selectedProduct.fax ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.fax ?? '') == '' ? 'Not available' : selectedProduct.fax}</div>
                    </Col>                    
                </Row>
                //<Row>
                //    <Col xs={2}>
                //        Tax Reg #:
                //    </Col>
                //    <Col xs={10}>
                //        <div style={{ color: ((selectedProduct.taxRegNumber ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.taxRegNumber ?? '') == '' ? 'Not available' : selectedProduct.taxRegNumber}</div>
                //    </Col>
                //</Row>
                <Row>
                    <Col xs={3}>
                        CDEX Sender Code:
                    </Col>
                    <Col xs={9}>
                        <div style={{ color: ((selectedProduct.cdexSenderCode ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.cdexSenderCode ?? '') == '' ? 'Not available' : selectedProduct.cdexSenderCode}</div>
                    </Col>
                </Row>
            </>
        );
    }

    //render detail pane (cdexTransactionCodes list)
    const showDataEdit = () => {
        return (
            <>
                {/* Parent-product header for the transactions pane. Static (the ul handles its own scroll).
                    Minimal style — small uppercase label + bold product name + gold CDEX chip. */}
                <div style={{
                    backgroundColor: '#ffffff',
                    borderBottom: `2px solid ${COLORS.gold}`,
                    padding: '6px 4px 8px 4px',
                    marginBottom: '4px',
                    color: '#6b7280',
                    letterSpacing: '.05em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    fontSize: props.embedded ? '.65rem' : '.7rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    <span>Transactions under</span>
                    <span style={{
                        color: '#1f2937',
                        textTransform: 'none',
                        fontSize: props.embedded ? '.85rem' : '.95rem',
                        fontWeight: 700,
                        letterSpacing: 'normal',
                    }}>
                        {selectedProduct.productDesc}
                    </span>
                    {selectedProduct.cdexProductCode ? (
                        <span style={{
                            padding: '1px 8px',
                            backgroundColor: COLORS.gold,
                            color: '#1f2937',
                            borderRadius: '10px',
                            fontSize: props.embedded ? '.7rem' : '.75rem',
                            fontWeight: 700,
                            letterSpacing: 'normal',
                            textTransform: 'none',
                        }}>
                            {selectedProduct.cdexProductCode}
                        </span>
                    ) : null}
                </div>
                {/* ul height reduced ~60px to make room for the header above so the
                    right pane's total height stays aligned with the master pane. */}
                <ul style={{ listStyleType: "none", height: props.embedded ? "330px" : "455px", marginRight: '12px', overflowY: 'auto', overflowX: 'hidden' }}>
                    {/* Sticky filter / sort row — same pattern as the master pane. */}
                    <li className="dc-filter-row" style={filterRowLiStyle}>
                        <Row>
                            {/* Interest Type Desc (transactionDesc) — search box + sort cycle */}
                            <Col xs={4} style={{ paddingRight: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder="Search..."
                                        value={transactionDescFilter}
                                        onChange={(e) => setTransactionDescFilter(e.target.value)}
                                        style={{ ...filterInputStyle, flex: 1 }}
                                    />
                                    <FontAwesomeIcon
                                        icon={sortIconFor(transactionSortBy, transactionSortDir, 'transactionDesc', { defaultAsc: true })}
                                        title={transactionSortBy === 'transactionDesc' && transactionSortDir === 'desc' ? 'Sorted descending — click to restore default (ascending)' : 'Currently ascending — click to sort descending'}
                                        style={filterSortIconStyle(transactionSortBy === 'transactionDesc')}
                                        onClick={() => cycleTransactionSortFromAsc('transactionDesc')}
                                    />
                                </div>
                            </Col>

                            {/* CDEX Transaction Code — dropdown + sort cycle */}
                            <Col xs={3} style={{ paddingRight: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Form.Select
                                        size="sm"
                                        value={cdexTransactionCodeFilter}
                                        onChange={(e) => setCdexTransactionCodeFilter(e.target.value)}
                                        style={filterSelectStyle}
                                    >
                                        <option value="">All</option>
                                        {distinctCdexTransactionCodes.map((code, i) => (
                                            <option key={i} value={code}>{code === '' ? '(blank)' : code}</option>
                                        ))}
                                    </Form.Select>
                                    <FontAwesomeIcon
                                        icon={sortIconFor(transactionSortBy, transactionSortDir, 'cdexTransactionCode')}
                                        title={transactionSortBy === 'cdexTransactionCode' ? (transactionSortDir === 'asc' ? 'Sorted ascending — click for descending' : 'Sorted descending — click to clear') : 'Click to sort ascending'}
                                        style={filterSortIconStyle(transactionSortBy === 'cdexTransactionCode')}
                                        onClick={() => cycleTransactionSort('cdexTransactionCode')}
                                    />
                                </div>
                            </Col>

                            {/* REV Line Type — dropdown + sort cycle */}
                            <Col xs={3} style={{ paddingRight: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Form.Select
                                        size="sm"
                                        value={revLineTypeFilter}
                                        onChange={(e) => setRevLineTypeFilter(e.target.value)}
                                        style={filterSelectStyle}
                                    >
                                        <option value="">All</option>
                                        {distinctRevLineTypeCodes.map((code, i) => (
                                            <option key={i} value={code}>{code === '' ? '(blank)' : (revLineTypeLabel(code) || code)}</option>
                                        ))}
                                    </Form.Select>
                                    <FontAwesomeIcon
                                        icon={sortIconFor(transactionSortBy, transactionSortDir, 'revLineTypeCode')}
                                        title={transactionSortBy === 'revLineTypeCode' ? (transactionSortDir === 'asc' ? 'Sorted ascending — click for descending' : 'Sorted descending — click to clear') : 'Click to sort ascending'}
                                        style={filterSortIconStyle(transactionSortBy === 'revLineTypeCode')}
                                        onClick={() => cycleTransactionSort('revLineTypeCode')}
                                    />
                                </div>
                            </Col>

                            {/* Apply to All — no filter / no sort, just an empty cell */}
                            <Col xs={2}></Col>
                        </Row>
                    </li>

                    {visibleTransactions.length === 0 ? (
                        <li style={{ paddingLeft: '20px', marginLeft: '-42px', color: '#9ca3af', fontStyle: 'italic', fontSize: props.embedded ? '.72rem' : '.8rem' }}>
                            No transactions match the current filters.
                        </li>
                    ) : null}
                    {visibleTransactions.map((cdexTransactionCode) => {
                        const index = cdexTransactionCode._originalIndex;
                        return (
                        <li key={index} ref={(index == 0 ? companyTopRef : null)}
                            onMouseOver={() => setHoveredTransactionIndex(index)}
                            onMouseLeave={() => setHoveredTransactionIndex(null)}
                            style={{ paddingLeft: '20px', marginLeft: '-42px', marginRight: '0px', fontSize: props.embedded ? '.72rem' : '.8rem' }}>
                            <Row style={{
                                borderTop: hoveredTransactionIndex == index ? `2px solid ${COLORS.gold}` : '2px solid transparent',
                                borderBottom: hoveredTransactionIndex == index ? `2px solid ${COLORS.gold}` : '1px solid #e5e7eb',
                                backgroundColor: index % 2 === 0 ? '#fafbfc' : 'transparent',
                                color: '#374151',
                                paddingTop: '4px',
                                paddingBottom: '4px',
                                transition: 'border-color 120ms ease'
                            }} >
                                {/* Interest Type Code column hidden for _dc */}
                                <Col xs={4} style={{ display: 'flex', alignItems: 'center' }}>
                                    <div>{cdexTransactionCode.transactionDesc}</div>
                                </Col>
                                <Col xs={3} >
                                    {(() => {
                                        if (!cdexTransactionCode.isReadOnly) {
                                            return <Form.Group className={props.embedded ? "mb-0" : "mb-3"} controlId="cdexTransactionCode">
                                                <Form.Control name="cdexTransactionCode" size="sm" aria-label="CDEX Transaction Code" value={cdexTransactionCode.cdexTransactionCode} style={props.embedded ? { width: '100%', fontSize: '.78rem', padding: '0px 6px', height: '22px', lineHeight: 1.2 } : { width: '100' }} onChange={(e) => handleTransactionChange(index, e)} />
                                            </Form.Group>
                                        }
                                        else {
                                            return <div>{cdexTransactionCode.cdexTransactionCode}</div>;
                                        }
                                    })()}
                                </Col>
                                <Col xs={3} >
                                    {(() => {
                                        if (!cdexTransactionCode.isReadOnly) {
                                            return <Form.Group className={props.embedded ? "mb-0" : "mb-3"} controlId="companyCode">
                                                <Form.Select name="revLineTypeCode" size="sm" aria-label="REV Line Type" value={cdexTransactionCode.revLineTypeCode} style={props.embedded ? { fontSize: '.78rem', padding: '0px 22px 0px 6px', height: '22px', lineHeight: 1.2, backgroundPosition: 'right 4px center' } : undefined} onChange={(e) => handleTransactionChange(index, e)} >
                                                    {revLineTypes.map((type, i) => (
                                                        <option key={i} value={type.code}>{type.value}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        }
                                        else {
                                            switch (cdexTransactionCode.revLineTypeCode) {
                                                case 'R':
                                                    return <div>REV</div>
                                                case 'T':
                                                    return <div>TAX</div>
                                                case 'D':
                                                    return <div>DED</div>
                                                case 'N':
                                                    return <div>NET</div>
                                                default:
                                                    return <div></div>
                                            }
                                        }
                                    })()}
                                </Col>
                                {/* Apply-to-all column — checkbox + inline label, only when
                                    the row is editable. Read-only rows leave the cell empty. */}
                                <Col xs={2} style={{ display: 'flex', alignItems: 'center' }}>
                                    {!cdexTransactionCode.isReadOnly && (
                                        <Form.Check
                                            type="checkbox"
                                            id={`applyToAll-tx-${index}`}
                                            label="Apply to all products"
                                            title="Apply this change to every product with this transaction"
                                            checked={!!cdexTransactionCode.applyToAll}
                                            onChange={(e) => handleTransactionApplyToAllChange(index, e.target.checked)}
                                            style={{ fontSize: '.68rem', color: '#374151', margin: 0, lineHeight: 1.15 }}
                                        />
                                    )}
                                </Col>
                            </Row>
                        </li>
                        );
                    })}
                </ul>
            </>
        );
    }

    //pagination example
    //https://github.com/lukaaspl/ellipsis-pagination/blob/master/src/components/Pagination.js

    //render input criteria
    const showInputCriteria = () => {
        const addBtn = {
            ...btnGold,
            fontSize: props.embedded ? '.8rem' : undefined,
            padding: props.embedded ? '2px 12px' : undefined,
            marginLeft: '8px',
            whiteSpace: 'nowrap'
        };
        return (
            <Container fluid>
                <Row>
                    <Col xs={5}>
                        <Button
                            size='sm'
                            style={{ ...btnGold, minWidth: props.embedded ? '160px' : '200px', padding: props.embedded ? '2px 12px' : undefined, fontSize: props.embedded ? '.8rem' : undefined, whiteSpace: 'nowrap' }}
                            onMouseEnter={e => Object.assign(e.target.style, boxshadowHover)}
                            onMouseLeave={e => Object.assign(e.target.style, boxshadowUnHover(btnGold))}
                            disabled={!anyProductDirty}
                            onClick={anyProductDirty ? () => updateProducts() : null}
                        >
                            Map Product Codes
                        </Button>
                        <Button
                            size='sm'
                            style={addBtn}
                            onMouseEnter={e => Object.assign(e.target.style, boxshadowHover)}
                            onMouseLeave={e => Object.assign(e.target.style, boxshadowUnHover(btnGold))}
                            onClick={handleAddProductCode}
                        >
                            Add Product Code
                        </Button>
                    </Col>
                    <Col xs={7}>
                        <div className="d-flex align-items-center">
                            <Button
                                size='sm'
                                style={{ ...btnGold, minWidth: props.embedded ? '160px' : '200px', padding: props.embedded ? '2px 12px' : undefined, fontSize: props.embedded ? '.8rem' : undefined, whiteSpace: 'nowrap' }}
                                onMouseEnter={e => Object.assign(e.target.style, boxshadowHover)}
                                onMouseLeave={e => Object.assign(e.target.style, boxshadowUnHover(btnGold))}
                                disabled={!anyTransactionDirty}
                                onClick={anyTransactionDirty ? () => updateTransactions() : null}
                            >
                                Map Transaction Codes
                            </Button>
                            <Button
                                size='sm'
                                style={addBtn}
                                onMouseEnter={e => Object.assign(e.target.style, boxshadowHover)}
                                onMouseLeave={e => Object.assign(e.target.style, boxshadowUnHover(btnGold))}
                                onClick={handleAddTransactionCode}
                            >
                                Add Transaction Code
                            </Button>
                            {/* XLS download — right-aligned via ms-auto. Reuses the override
                                page's endpoint; rows are built from the current filtered/sorted
                                view so the export matches what the user has on screen. */}
                            <div className="ms-auto">
                                {downloadDataStatus === 'loading' ? (
                                    <Button size='sm' variant="primary" style={{ backgroundColor: 'green', borderColor: 'green' }}>
                                        <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                    </Button>
                                ) : (
                                    /* Instant CSS-only tooltip — see .dc-inline-tip rule
                                       in the <style> block above. */
                                    <span className="dc-inline-tip">
                                        <Button
                                            size='sm'
                                            variant="primary"
                                            onClick={handleExportXls}
                                            style={{ backgroundColor: 'green', borderColor: 'green' }}
                                        >
                                            <FontAwesomeIcon icon={faFileExcel} />
                                        </Button>
                                        <span className="dc-inline-tip-text">Download CDEX Global Mappings as XLS</span>
                                    </span>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
                <Row style={{fontSize: props.embedded ? '.8rem' : '.9rem', marginTop: props.embedded ? '8px' : '20px', color: '#f1f1f1', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em'}} >
                    <Col xs={5}>
                        <Row>
                            <Col xs={7}>Product Desc</Col>
                            <Col xs={5} style={{ textAlign: 'center' }}>CDEX Product Code</Col>
                        </Row>
                    </Col>
                    <Col xs={7} style={{borderLeft: '1px solid #e5e7eb'}} >
                        <Row>
                            <Col xs={4}>Interest Type Desc</Col>
                            <Col xs={3}>CDEX Transaction Code</Col>
                            <Col xs={3}>REV Line Type</Col>
                            {/* Apply-to-all column header intentionally blank — the inline
                                checkbox label tells the story when the row is in edit mode. */}
                            <Col xs={2}></Col>
                        </Row>
                    </Col>
                </Row>

            </Container>
        );
    }

    return (
        <div>
            {/* Force consistent 24px height on every input/select inside the sticky
                filter row. Bootstrap's .form-control-sm sets a min-height that wins
                over inline `height` due to CSS specificity; !important clamps it. */}
            <style>{`
                .dc-filter-row .form-control-sm,
                .dc-filter-row .form-select-sm {
                    height: 24px !important;
                    min-height: 24px !important;
                    max-height: 24px !important;
                    padding-top: 0 !important;
                    padding-bottom: 0 !important;
                    line-height: 1.2 !important;
                    box-sizing: border-box !important;
                }
                /* Instant CSS-only tooltip for the XLS button — see
                   CdexCodesOverride_dc.js for the rationale (no native-title delay,
                   no Popper-induced scrollbar flash). */
                .dc-inline-tip { position: relative; display: inline-block; }
                .dc-inline-tip > .dc-inline-tip-text {
                    position: absolute;
                    right: calc(100% + 8px);
                    top: 50%;
                    transform: translateY(-50%);
                    background: #1f2937;
                    color: #f9fafb;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: .75rem;
                    line-height: 1.2;
                    white-space: nowrap;
                    pointer-events: none;
                    opacity: 0;
                    visibility: hidden;
                    z-index: 5;
                }
                .dc-inline-tip:hover > .dc-inline-tip-text {
                    opacity: 1;
                    visibility: visible;
                }
            `}</style>
            {!props.embedded && (
                <p style={{ fontSize: '1.75rem', fontWeight: '700', color: '#333333', margin: '16px 0 4px 0', textAlign: 'center' }}>Global NON-Operated CDEX Mappings</p>
            )}
            {(() => {
                switch (pageStatus) {
                    case 'loading':
                        return showPageLoading()
                    case 'ready':
                        return showTableContainer()
                    case 'error':
                        return showPageError()
                    default:
                        return <p>No Page Loaded.</p>
                }
            })()}

            {/* Add Product Code modal */}
            <Modal show={showAddProductModal} onHide={() => setShowAddProductModal(false)} centered backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Add Product Code</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3" controlId="addProductDesc">
                            <Form.Label>Product Desc</Form.Label>
                            <Form.Control
                                type="text"
                                size="sm"
                                value={addProductForm.productDesc}
                                onChange={e => setAddProductForm({ ...addProductForm, productDesc: e.target.value })}
                                autoFocus
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="addCdexProductCode">
                            <Form.Label>CDEX Product Code</Form.Label>
                            <Form.Control
                                type="text"
                                size="sm"
                                maxLength={2}
                                value={addProductForm.cdexProductCode}
                                onChange={e => setAddProductForm({ ...addProductForm, cdexProductCode: e.target.value })}
                            />
                        </Form.Group>
                        {modalError && (
                            <div style={{ color: '#dc3545', fontSize: '.85rem', marginTop: '4px' }}>{modalError}</div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowAddProductModal(false)} disabled={modalSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        style={{ ...btnGold }}
                        onClick={submitAddProductCode}
                        disabled={modalSubmitting}
                    >
                        {modalSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Transaction Code modal */}
            <Modal show={showAddTransactionModal} onHide={() => setShowAddTransactionModal(false)} centered backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Add Transaction Code</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Target product display + apply-to-all toggle */}
                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px 10px', marginBottom: '12px' }}>
                        <div style={{ fontSize: '.78rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.04em' }}>
                            Target
                        </div>
                        <div style={{ fontSize: '.95rem', fontWeight: 600, color: '#1f2937', marginTop: '2px', opacity: addTransactionForm.applyToAll ? 0.5 : 1 }}>
                            {addTransactionForm.applyToAll
                                ? '(applying to all product codes)'
                                : (selectedProduct ? selectedProduct.productDesc : '(no product selected)')}
                        </div>
                        <Form.Check
                            type="checkbox"
                            id="addTransactionApplyToAll"
                            label="Apply to all product codes"
                            checked={addTransactionForm.applyToAll}
                            onChange={e => setAddTransactionForm({ ...addTransactionForm, applyToAll: e.target.checked })}
                            style={{ marginTop: '8px', fontSize: '.85rem' }}
                        />
                    </div>
                    <Form>
                        <Form.Group className="mb-3" controlId="addTransactionDesc">
                            <Form.Label>Interest Type Desc</Form.Label>
                            <Form.Control
                                type="text"
                                size="sm"
                                value={addTransactionForm.transactionDesc}
                                onChange={e => setAddTransactionForm({ ...addTransactionForm, transactionDesc: e.target.value })}
                                autoFocus
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="addCdexTransactionCode">
                            <Form.Label>CDEX Transaction Code</Form.Label>
                            <Form.Control
                                type="text"
                                size="sm"
                                maxLength={3}
                                value={addTransactionForm.cdexTransactionCode}
                                onChange={e => setAddTransactionForm({ ...addTransactionForm, cdexTransactionCode: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="addRevLineTypeCode">
                            <Form.Label>REV Line Type</Form.Label>
                            <Form.Select
                                size="sm"
                                value={addTransactionForm.revLineTypeCode}
                                onChange={e => setAddTransactionForm({ ...addTransactionForm, revLineTypeCode: e.target.value })}
                            >
                                {revLineTypes.filter(t => t.code !== '').map((type, i) => (
                                    <option key={i} value={type.code}>{type.value}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        {modalError && (
                            <div style={{ color: '#dc3545', fontSize: '.85rem', marginTop: '4px' }}>{modalError}</div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowAddTransactionModal(false)} disabled={modalSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        style={{ ...btnGold }}
                        onClick={submitAddTransactionCode}
                        disabled={modalSubmitting}
                    >
                        {modalSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default CdexCodes_dc;
